import * as PropTypes from 'prop-types'
import classNames from 'classnames'
import Waypoint from 'react-waypoint'
import isEqual from 'react-fast-compare'
import { Subscribe } from 'unstated'

import sectionsStyles from './Section.css'
import { getAbsoluteTopOfElement } from '/javascripts/utils/getAbsoluteTopOfElement'

import { withPageJump } from '/react/utils/withPageJump'
import { withScrollDirection } from '/react/utils/withScrollDirection'
import { withViewport } from '/react/utils/withViewport'

import { MiniNavigationContainer } from '/react/library'

@withPageJump()
@withScrollDirection()
@withViewport()

export class Section extends Component {
  static propTypes = {
    centersChildren: PropTypes.bool,
    children: PropTypes.node.isRequired,
    color: PropTypes.string.isRequired,
    exceedsViewport: PropTypes.bool,
    fillsViewport: PropTypes.bool,
    id: PropTypes.string,
    isDark: PropTypes.bool,
    title: PropTypes.string.isRequired,
    wrapsContent: PropTypes.bool,
    wrapsContentLarge: PropTypes.bool,
    start: PropTypes.bool,
    showContactCta: PropTypes.bool,
    hasOverflowHidden: PropTypes.bool
  }

  sectionAbsoluteTop = 0
  sectionRef = React.createRef()
  onEnterScrollPosition = 0

  // Visibility offset is the vertical center of the screen
  visibilityOffset = (this.props.viewportHeight / 2) - 1  // -1 so bottom and top do not conflict with each other
  scrollTresholdOffset = (this.props.viewportHeight / 2)

  state = {
    isMounted: false,
    animateToEnd: false
  }

  shouldAnimateToEnd = () => {
    return (
      !this.state.animateToEnd &&
      this.props.pageIsJumping &&
      this.props.pageHasJumpedToPosition > this.sectionAbsoluteTop
    )
  }

  handleOnEnterForSiteHeader = (previousPosition, color) => {
    if (this.props.shouldBlockInViewportEvents()) return

    this.bus && this.bus.emit('updateSiteHeader', { isLight: color === 'light', showContactCta: !!this.props.showContactCta })
  }

  handleOnEnterForMiniNavigation = (miniNavigation, title, color) => {
    if (this.props.shouldBlockInViewportEvents()) return

    const sectionTitleIndex = miniNavigation.sectionTitles.indexOf(title)

    this.onEnterScrollPosition = window.pageYOffset

    miniNavigation.setState({ color })

    if (this.props.start) {
      miniNavigation.expand(sectionTitleIndex)
    } else {
      miniNavigation.collapse()
      miniNavigation.setActiveIndex(sectionTitleIndex)
    }
  }

  handleMountAndUpdate = () => {
    if (this.shouldAnimateToEnd()) {
      this.setState({
        animateToEnd: true
      })
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !(
      isEqual(this.props, nextProps) &&
      isEqual(this.state, nextState)
    )
  }

  componentDidUpdate() {
    this.handleMountAndUpdate()
  }

  async componentDidMount() {
    // Dynamically import because "window" is not defined when using SSR
    const module = await import('/javascripts/bus')
    this.bus = module.default

    this.setState({ isMounted: true })
    this.sectionAbsoluteTop = getAbsoluteTopOfElement(this.sectionRef.current)
    this.handleMountAndUpdate()
  }

  render() {
    const {
      animateToEnd,
      isMounted
    } = this.state

    if (!isMounted) return null

    const {
      centersChildren,
      children,
      color,
      exceedsViewport,
      hasOverflowHidden,
      fillsViewport,
      shouldBlockInViewportEvents,
      id,
      isDark,
      pageIsJumping,
      title,
      wrapsContent,
      wrapsContentLarge
    } = this.props



    const blockInViewportEvents = shouldBlockInViewportEvents()

    const childrenWithProps = React.Children.map(children, (child) => {
      if (typeof child.type === 'string') {
        return child
      } else {
        return React.cloneElement(child, {
          animateToEnd,
          blockInViewportEvents,
          pageIsJumping
        })
      }
    })

    return (
      <Subscribe to={[MiniNavigationContainer]}>
        {(miniNavigation) => (
          <Waypoint
            bottomOffset={this.visibilityOffset}
            topOffset={this.visibilityOffset}
            onEnter={() => this.handleOnEnterForMiniNavigation(miniNavigation, title, color)}
          >
            <div>
              <Waypoint
                topOffset={`${this.props.viewportIsTabletOrSmaller ? 44 : 87}px`}
                bottomOffset={`${this.props.viewportHeight - (this.props.viewportIsTabletOrSmaller ? 45 : 88)}px`}
                onEnter={({ previousPosition }) => this.handleOnEnterForSiteHeader(previousPosition, color)}
              >
                <section
                  className={classNames(
                    sectionsStyles.section,
                    { [sectionsStyles.sectionCentersChildren]: centersChildren },
                    { [sectionsStyles.sectionExceedsViewport]: exceedsViewport },
                    { [sectionsStyles.sectionFillsViewport]: fillsViewport },
                    { [sectionsStyles.sectionIsDark]: isDark },
                    { [sectionsStyles.sectionHasOverflowHidden]: hasOverflowHidden }
                  )}
                  id={id}
                >
                  <div
                    className={sectionsStyles.sectionWrapper}
                    ref={this.sectionRef}
                  >
                    {(wrapsContent || wrapsContentLarge)
                      ? (
                        <div className={classNames(
                          'u-layout-contentWrapper',
                          { 'u-layout-contentWrapper--large': wrapsContentLarge }
                        )}>
                          {childrenWithProps}
                        </div>
                      )
                      : childrenWithProps
                    }
                  </div>
                </section>
              </Waypoint>
            </div>
          </Waypoint>
        )}
      </Subscribe>
    )
  }
}
